import type { GetThirdPartyAuthConfigFunction } from '@shopee-user/business-customization-types';
import { isWebBridgeSupported } from '@shopee/web-bridge-sdk';
// DO NOT import from with-web-view index directly, it will cause circular dependency
import { AuthMethod } from '@shopee-user/with-web-view/src/constants';
import type { WithUserWebViewProps } from '@shopee-user/with-web-view/src/types';
import { AuthenticateChannel } from '@tob/shared/thirdparty-auth/core/types';

// https://confluence.shopee.io/display/SAUT/%5BFor+User+FE+Reference%5D+FE+Integration+Guide
export const DEFAULT_SHOPEE_AUTH_SCOPES = ['userProfile'];

export const getAuthConfigFromNative = async ({
  method,
  webbridge,
}: {
  method: AuthMethod;
  webbridge: {
    getAuthConfig: WithUserWebViewProps['webViewGetAuthConfig'];
  };
}) => {
  const { config } = await webbridge.getAuthConfig({ method });
  if (!config) return undefined;
  const { enabled, ...rest } = config;
  return enabled ? { ...rest } : undefined;
};

export const getShopeeAuthConfig = async ({
  baseConfig,
  webbridge,
}: {
  baseConfig: {
    supported: boolean;
    clientId: string;
    scopes?: string[];
    displayNameTransifyKey: string;
    hostAppCallbackUrl: string;
  };
  webbridge?: {
    getAuthConfig: WithUserWebViewProps['webViewGetAuthConfig'];
  };
}) => {
  const baseConfigWithScopes: Required<typeof baseConfig> = {
    ...baseConfig,
    scopes: baseConfig.scopes || DEFAULT_SHOPEE_AUTH_SCOPES,
  };

  if (!isWebBridgeSupported() || !webbridge) {
    return {
      ...baseConfigWithScopes,
    };
  }

  const nativeConfig = await getAuthConfigFromNative({
    method: AuthMethod.ShopeeOAuth,
    webbridge,
  });
  return {
    ...baseConfigWithScopes,
    supported: !!nativeConfig,
    ...(nativeConfig
      ? {
          clientId: nativeConfig.client_id || baseConfigWithScopes.clientId,
          hostAppCallbackUrl:
            nativeConfig.redirect_url ||
            baseConfigWithScopes.hostAppCallbackUrl,
        }
      : {}),
  };
};

export const getThirdPartyAuthConfig: GetThirdPartyAuthConfigFunction = async () => {
  return {
    [AuthenticateChannel.Shopee]: {
      supported: false,
      clientId: '',
      scopes: DEFAULT_SHOPEE_AUTH_SCOPES,
      displayNameTransifyKey: '',
      hostAppCallbackUrl: '',
    },
  };
};
